// « Forfaits et prix » / « Options et conditions »: the price questions are built from the live catalog
// (public.packages / public.package_addons, active rows), so a price changed in Paramètres changes the quiz.
import type { QuizOption, QuizQuestion } from "../types";

const K = "gavion.training.product.catalog";

export type CatalogPackageLike = {
  id: number;
  name: string;
  line: string;
  monthly_price: number | string;
  active?: boolean;
  position?: number;
};
export type CatalogAddonLike = {
  id: number;
  name: string;
  unit_price: number | string;
  billing: string;
  active?: boolean;
  position?: number;
};

const byPosition = (
  a: { id: number; position?: number },
  b: { id: number; position?: number },
) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id;

/** The 3 values of `pool` closest to `answer` (ties: the lower first), padded with derived values when short. */
export function distractors(answer: number, pool: readonly number[]): number[] {
  const out = [...new Set(pool.map(Number))]
    .filter((v) => v !== answer)
    .sort((a, b) => Math.abs(a - answer) - Math.abs(b - answer) || a - b)
    .slice(0, 3);
  for (const d of [
    answer * 2,
    Math.round(answer / 2),
    answer + 10,
    answer + 20,
    answer + 30,
  ]) {
    if (out.length >= 3) break;
    if (d > 0 && d !== answer && !out.includes(d)) out.push(d);
  }
  return out;
}

const moneyOption = (v: number): QuizOption => ({
  id: `p${v}`,
  labelKey: `${K}.option`,
  labelArgs: { value: { money: v } },
});

const priceQuestion = (
  id: string,
  name: string,
  price: number,
  pool: readonly number[],
  explanationKey: string,
  promptKey: string,
): QuizQuestion => ({
  id,
  promptKey,
  promptArgs: { name: { text: name } },
  options: [price, ...distractors(price, pool)]
    .sort((a, b) => a - b)
    .map(moneyOption),
  answer: `p${price}`,
  explanationKey,
});

export function catalogQuestions(
  packages: readonly CatalogPackageLike[],
  addons: readonly CatalogAddonLike[],
): { pricing: QuizQuestion[]; addonPrices: QuizQuestion[] } {
  const pk = packages.filter((p) => p.active !== false).sort(byPosition);
  const ad = addons.filter((a) => a.active !== false).sort(byPosition);
  const prices = pk.map((p) => Number(p.monthly_price));
  const pricing = pk.map((p) =>
    priceQuestion(
      `price_${p.id}`,
      p.name,
      Number(p.monthly_price),
      prices,
      `${K}.explanation`,
      `${K}.price_prompt`,
    ),
  );
  // « Quel forfait coûte X ? »: the first package of each line, against the 3 packages closest in price
  const firsts = pk.filter(
    (p, i) => pk.findIndex((x) => x.line === p.line) === i,
  );
  for (const p of firsts) {
    const price = Number(p.monthly_price);
    const others = pk
      .filter((x) => x.id !== p.id && Number(x.monthly_price) !== price) // a same-price package would be a 2nd right answer
      .sort(
        (a, b) =>
          Math.abs(Number(a.monthly_price) - price) -
            Math.abs(Number(b.monthly_price) - price) || a.id - b.id,
      )
      .slice(0, 3);
    if (others.length < 3) continue; // fewer than 3 other prices: no « which package » question
    pricing.push({
      id: `package_${p.id}`,
      promptKey: `${K}.package_prompt`,
      promptArgs: { price: { money: price } },
      options: [p, ...others]
        .sort(
          (a, b) =>
            Number(a.monthly_price) - Number(b.monthly_price) || a.id - b.id,
        )
        .map((x) => ({
          id: `k${x.id}`,
          labelKey: `${K}.option`,
          labelArgs: { value: { text: x.name } },
        })),
      answer: `k${p.id}`,
      explanationKey: `${K}.explanation`,
    });
  }
  const addonPool = ad.map((a) => Number(a.unit_price));
  const addonPrices = ad.map((a) =>
    priceQuestion(
      `addon_${a.id}`,
      a.name,
      Number(a.unit_price),
      addonPool,
      a.billing === "per_unit_in_month"
        ? `${K}.addon_unit_explanation`
        : `${K}.explanation`,
      `${K}.addon_prompt`,
    ),
  );
  return { pricing, addonPrices };
}
